"use client";

import { logo_white } from "@/images";
import { cn } from "@/lib";
import { replaceImage } from "@/utils";
import Image from "next/image";
import { useState } from "react";

/**
 * Resolve whatever a caller passed into something <Image> will accept.
 *
 * Callers reach in through optional chains - `poster?.[0]?.url` - so `src`
 * arrives as undefined, null or "" whenever a record has no image. Passing any
 * of those straight through is the fault this guards:
 *
 *   ""        -> React warns, and the browser resolves it against the current
 *                URL and re-downloads the whole PAGE as if it were an image.
 *   undefined -> next/image logs `Image is missing required "src" property`
 *                and renders a broken element.
 *
 * A static import (the logo) is a StaticImageData object, not a string, so it
 * is handed back untouched - `replaceImage` would stringify it into nonsense.
 */
const resolveSrc = (src: unknown) => {
    if (!src) return null;
    if (typeof src !== 'string') return src;

    const trimmed = src.trim();
    if (!trimmed) return null;

    return replaceImage(trimmed) || null;
};

const CommonImage =({
    src,
    alt,
    ...props
}: any) => {
    const [imageError, setImageError] = useState(false);

    const resolved = resolveSrc(src);
    // A missing image and a broken one look the same to a reader, so they get
    // the same treatment rather than one of them reaching <Image> as empty.
    const showFallback = imageError || resolved === null;

    return (
        <Image
            {...props}
            // Always ship a non-empty alt for accessibility & image SEO; callers
            // that pass a descriptive alt keep theirs.
            alt={alt || 'Greet event'}
            src={showFallback ? logo_white : resolved}
            onError={() => setImageError(true)}
            className={cn(props.className, showFallback && 'bg-black object-contain')}
        />
    );
}

export default CommonImage;
